/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { DateTime } from 'luxon';
import { StyleSheet } from 'aphrodite';
import { TextInput, Dropdown } from '@blend/enterprise-components';
import { WrappedInput } from '@components/common/enterprise/WrapperInput/WrapperInput';
import { getLocalDateTime, getTimeZone } from '@utils/time';
import { compact, range } from '@utils/lodash';
import { TypeaheadSearch } from '@components/TypeaheadSearch/TypeaheadSearch';

export const styles = StyleSheet.create({
  fieldWrapper: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
  },
  half: {
    flexBasis: '48%',
  },
});

export const DEFAULT_LOCAL_TIME = '07:00:00';

const standardInputConfig = {
  component: WrappedInput,
  containerStyles: [styles.fieldWrapper],
  labelSize: 'sm',
};

export const scheduleDateInputConfig = {
  ...standardInputConfig,
  customComponentProps: { type: 'text', disabled: true },
  inputComponent: TextInput,
  label: 'Date',
  labelSize: 'sm',
  name: 'date',
};

export const searchNotaryInputConfig = {
  ...standardInputConfig,
  inputComponent: TypeaheadSearch,
  label: 'Search',
  labelSize: 'sm',
  name: 'search',
};

export const notaryNameInputConfig = {
  ...standardInputConfig,
  customComponentProps: { type: 'text', disabled: true },
  inputComponent: TextInput,
  label: 'Name',
  labelSize: 'sm',
  name: 'notaryName',
};

export const notaryEmailInputConfig = {
  ...standardInputConfig,
  customComponentProps: { type: 'text', disabled: true },
  inputComponent: TextInput,
  label: 'Email',
  labelSize: 'sm',
  name: 'notaryEmail',
};

const convertTimeZoneOfTimeSlot = (date: string, timeSlots: string[], fromTimeZone: string, toTimeZone: string) => {
  return timeSlots.map(timeSlot => {
    return DateTime.fromISO(`${date}T${timeSlot}`, { zone: fromTimeZone }).setZone(toTimeZone).toFormat('HH:mm:ss');
  });
};

export const getTimeOptions = (
  scheduleDateTxt: string,
  timeZone?: string,
  preferredTimeZone?: string,
  preferredTimeSlots?: string[],
): { text: string; value: string }[] => {
  if (!timeZone) {
    return [];
  }
  const scheduleDate = DateTime.fromISO(scheduleDateTxt, { zone: timeZone });
  // as time schedule is synced with eclose, this stepMinutes must be the same as in eClose
  const stepMinutes = 30;

  const preferredTimeSlotsInSelectedTimeZone =
    preferredTimeZone && preferredTimeSlots
      ? convertTimeZoneOfTimeSlot(scheduleDateTxt, preferredTimeSlots, preferredTimeZone, timeZone)
      : [];

  return compact(
    range(0, 48).map(v => {
      const dateTime = scheduleDate.set({ hour: 0, minute: 0, second: 0 }).plus({ minutes: v * stepMinutes });

      // Skip time slots that are in the past (more than 30 minutes ago)
      const inThePast = getLocalDateTime().setZone(timeZone).diff(dateTime, 'minutes').minutes >= 30;
      if (inThePast) {
        return;
      }
      const value = dateTime.toFormat('HH:mm:ss');
      const isPreferredTimeSlot = preferredTimeSlotsInSelectedTimeZone.includes(value);
      const text = `${dateTime.toFormat('h:mm a')}` + (isPreferredTimeSlot ? ' - borrower preferred' : '');
      return { text, value };
    }),
  );
};

export const getScheduledTimeConfig = (
  scheduleDate: string,
  timeZone: string | undefined,
  initialLocalTime: string | undefined,
  preferredTimeZone: string | undefined,
  preferredTimeSlots: string[] | undefined,
  onClick?: () => void,
) => {
  const timeOptions = getTimeOptions(scheduleDate, timeZone, preferredTimeZone, preferredTimeSlots);
  // when room.scheduledAt does not have time or room.scheduledAt is in the past, don't prepopulate the dropbox
  const valueRequired = !!initialLocalTime && timeOptions.some(option => option.value === initialLocalTime);
  return {
    ...standardInputConfig,
    customComponentProps: {
      options: timeOptions,
      valueRequired,
      onClick,
    },
    containerStyles: [styles.half],
    inputComponent: Dropdown,
    label: 'Time',
    name: 'localTime',
    // value can be initial value and it may not be in the dropdown list (if that initial value is the time in the past)
    validate: (value: string, allValues: { timeZone?: string; _localTimeTouched?: boolean }) => {
      if (value === DEFAULT_LOCAL_TIME && !allValues._localTimeTouched) {
        // if the value is the default time and the _localTimeTouched is not set, it means the user has not interacted with the dropdown yet
        return 'Please click here to confirm your time selection';
      }
      const currentTimeOptions = getTimeOptions(scheduleDate, allValues.timeZone);
      return value && currentTimeOptions.some(option => option.value === value) ? undefined : 'This field is required';
    },
  };
};

// As schedule time is synced with eClose, the following list of timeZone must be the same as the list of time zones in eClose.
const timeZoneOptions = [
  { text: 'Pacific Time (America/Los Angeles)', value: 'America/Los_Angeles' },
  { text: 'Eastern Time (America/New York)', value: 'America/New_York' },
  { text: 'Central Time (America/Chicago)', value: 'America/Chicago' },
  { text: 'Mountain Time - Colorado (America/Denver)', value: 'America/Denver' },
  { text: 'Mountain Time - Arizona (America/Phoenix)', value: 'America/Phoenix' },
  { text: 'Alaska Time (America/Anchorage)', value: 'America/Anchorage' },
  { text: 'Hawaii Time (America/Honolulu)', value: 'Pacific/Honolulu' },
  { text: 'Hawaii Aleutian Time (America/Adak)', value: 'America/Adak' },
  { text: 'Wake Island Time (Pacific/Wake)', value: 'Pacific/Wake' },
  { text: 'Chamorro Time/Guam Time (Pacific/Guam)', value: 'Pacific/Guam' },
  { text: 'Samoa Time (Pacific/Pago_Pago)', value: 'Pacific/Pago_Pago' },
  { text: 'Atlantic Time (America/Puerto_Rico)', value: 'America/Puerto_Rico' },
];

export const scheduleTimeZoneConfig = {
  ...standardInputConfig,
  component: WrappedInput,
  customComponentProps: { options: timeZoneOptions, valueRequired: true },
  containerStyles: [styles.half],
  inputComponent: Dropdown,
  label: 'Time zone',
  name: 'timeZone',
};

export const getDropdownInitialTimeZone = (timeZone?: string): string => {
  const browserTimeZone = getTimeZone();

  // When timeZone is not specify, try to use browser time zone as default time zone
  const initialTimeZone = timeZoneOptions.find(
    opt => opt.value.toLowerCase() === (timeZone || browserTimeZone).toLowerCase(),
  )?.value;

  // fallback to PST timezone
  return initialTimeZone || timeZoneOptions[0].value;
};
